import React, { useState } from "react";
import styled from "styled-components";
import * as yup from "yup";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import axios from "lib/api";
import trackAnalytics from "trackAnalytics";
import { Button, GenericError } from "components";
import { SeparateLine, ButtonRow } from "styles/elements/main";
import { FormStyle } from "styles/forms/main";
import Spinner from "components/Spinner";
import validationSchema from "./FormSchema";
import mapUiToData from "./mapUiToData";
import {
    DriverIsExist,
    OthersSection,
    PaymentMethods,
    PersonalInfo,
    WorkingInfo,
} from "./components";
import TruckOwner from "./components/TruckOwner";

/**
 * A component that allows the user to create a driver profile.
 * @param {object} onClose - A function to close the modal.
 * @param {function} setOnSuccess - A function to set the success state of the modal.
 * @returns None
 */
function CreateDriverProfile({ onClose, setOnSuccess, handleDiscardUpdate }) {
    const [driverData, setDriverData] = useState({});
    const [isEditable, setIsEditable] = useState(false);
    const [driverExist, setDriverExist] = useState(false);
    const [errors, setErrors] = useState({});
    const [backendErrors, setBackErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    /**
     * Updates the driverData state object with the new name and value from the form input.
     * @param {object} e - The event object from the form input.
     * @returns None
     */
    const handleDataFroSubmit = (e, fieldName) => {
        if (!fieldName) {
            const { name, value } = e?.target;
            setDriverData({
                ...driverData,
                [name]: value,
            });
            yup.reach(validationSchema, name)
                .validate(value)
                .then(() => setErrors({ ...errors, [name]: "" }))
                .catch((err) => setErrors({ ...errors, [name]: err.message }));
        } else {
            setDriverData({
                ...driverData,
                [fieldName]: e,
            });
            yup.reach(validationSchema, fieldName)
                .validate(e)
                .then(() => setErrors({ ...errors, [fieldName]: "" }))
                .catch((err) => setErrors({ ...errors, [fieldName]: err.message }));
        }
    };
    /**
     * Handles the submission of a driver form by validating
     * the form data and sending a POST request to the server.
     * @returns None
     */
    const handleFormSubmition = async (e) => {
        trackAnalytics(`Driver_profile_submit_creation`);

        e.preventDefault();
        validationSchema
            .validate(driverData, { abortEarly: false })
            .then(async () => {
                setIsLoading(true);
                const mapedData = await mapUiToData(driverData, driverData?.nationality);
                axios
                    .post("/drivers", mapedData, {
                        params: {
                            include: "supplier,paymentMethod",
                        },
                    })
                    .then(() => {
                        setIsLoading(false);
                        onClose();
                        setOnSuccess(true);
                    })
                    .catch((err) => {
                        setIsLoading(false);
                        setBackErrors(err?.errors);
                    });
            })
            .catch((err) => {
                const errorslist = err?.inner?.reduce(
                    (acc, error) => ({
                        ...acc,
                        [error?.path]: error?.message,
                    }),
                    {}
                );

                setErrors(errorslist);
            });
    };
    return (
        <>
            {driverExist && <DriverIsExist />}
            <FormStyle>
                <PersonalInfo
                    driverData={driverData}
                    setDriverData={setDriverData}
                    handleDataFroSubmit={handleDataFroSubmit}
                    isEditable={isEditable}
                    setIsEditable={setIsEditable}
                    setDriverExist={setDriverExist}
                    errors={errors}
                    isQARole={false}
                />
                <SeparateLine />
                <WorkingInfo
                    driverData={driverData}
                    handleDataFroSubmit={handleDataFroSubmit}
                    isEditable={isEditable}
                    errors={errors}
                    isQARole={false}
                />
                <SeparateLine />

                <PaymentMethods
                    driverData={driverData}
                    setDriverData={setDriverData}
                    handleDataFroSubmit={handleDataFroSubmit}
                    isEditable={isEditable}
                    errors={errors}
                    isQARole={false}
                />
                <SeparateLine />
                <OthersSection
                    isEditable={isEditable}
                    handleDataFroSubmit={handleDataFroSubmit}
                    driverData={driverData?.notes}
                    isQARole={false}
                />
                <SeparateLine />
                <TruckOwner
                    driverData={driverData}
                    handleDataFroSubmit={handleDataFroSubmit}
                    isEditable={isEditable}
                    errors={errors}
                />
                {backendErrors && <GenericError errorMassege={backendErrors} />}
                <ButtonRow>
                    <Button
                        onChange={handleDiscardUpdate}
                        name="Cancel"
                        btnStyle="secondBtn"
                        type="button"
                    />
                    {isEditable && (
                        <Button
                            btnStyle={isLoading ? "disabled" : "primaryBtn"}
                            type="submit"
                            name={isLoading ? <Spinner /> : "Add"}
                            disabled={isLoading}
                            icon={!isLoading && <AddCircleOutlineOutlinedIcon />}
                            onChange={handleFormSubmition}
                        />
                    )}
                </ButtonRow>
            </FormStyle>
        </>
    );
}
const Title = styled.h2`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #006bb4;
`;

export default CreateDriverProfile;
